import { useState } from "react";
import { motion } from "framer-motion";
import { Gift, ArrowRight, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const voucherAmounts = [50, 75, 100, 150, 200];

export default function GiftVouchersPage() {
  const [selectedAmount, setSelectedAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState("");
  const [isCustom, setIsCustom] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    purchaserName: "",
    purchaserEmail: "",
    recipientName: "",
    recipientEmail: "",
    message: "",
  });

  const handleInputChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setIsCustom(false);
    setCustomAmount("");
  };

  const handleCustomAmount = (e) => {
    setCustomAmount(e.target.value);
    setIsCustom(true);
    if (e.target.value) {
      setSelectedAmount(parseFloat(e.target.value));
    }
  };

  const getFinalAmount = () => {
    return isCustom && customAmount ? parseFloat(customAmount) : selectedAmount;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.purchaserName || !formData.purchaserEmail) {
      toast.error("Please fill in your name and email");
      return;
    }

    const amount = getFinalAmount();
    if (!amount || amount < 10) {
      toast.error("Please select a valid amount (minimum £10)");
      return;
    }

    setIsLoading(true);
    try {
      // Create voucher
      const voucherRes = await axios.post(`${API}/vouchers`, {
        amount: amount,
        purchaser_name: formData.purchaserName,
        purchaser_email: formData.purchaserEmail,
        recipient_name: formData.recipientName || null,
        recipient_email: formData.recipientEmail || null,
        message: formData.message || null,
      });

      // Create checkout session
      const checkoutRes = await axios.post(`${API}/checkout/voucher`, {
        voucher_id: voucherRes.data.id,
        origin_url: window.location.origin,
      });

      // Redirect to Stripe checkout
      window.location.href = checkoutRes.data.checkout_url;
    } catch (error) {
      console.error("Voucher error:", error);
      toast.error(error.response?.data?.detail || "Failed to create gift voucher");
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="gift-vouchers-page">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="text-xs tracking-[0.3em] uppercase text-primary mb-4">
            The Perfect Gift
          </p>
          <h1 className="heading-lg text-foreground mb-6">
            Gift <em className="italic">Vouchers</em>
          </h1>
          <p className="body-text text-muted-foreground max-w-xl mx-auto">
            Give the gift of relaxation and healing. Our gift vouchers can be used 
            for any service and are valid for 12 months from purchase.
          </p>
        </motion.div>

        <div className="max-w-3xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Amount Selection */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2 className="font-serif text-2xl mb-6">Choose Amount</h2>
              
              <div className="grid grid-cols-3 gap-3 mb-6">
                {voucherAmounts.map((amount) => (
                  <Button
                    key={amount}
                    variant={selectedAmount === amount && !isCustom ? "default" : "outline"}
                    className={`rounded-none py-6 ${
                      selectedAmount === amount && !isCustom
                        ? "bg-primary text-primary-foreground"
                        : "border-border hover:border-primary"
                    }`}
                    onClick={() => handleAmountSelect(amount)}
                    data-testid={`amount-${amount}`}
                  >
                    £{amount}
                  </Button>
                ))}
              </div>

              <div>
                <Label className="text-sm mb-2 block">Or enter custom amount</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    £
                  </span>
                  <Input
                    type="number"
                    min="10"
                    placeholder="Custom amount"
                    value={customAmount}
                    onChange={handleCustomAmount}
                    className="pl-8 rounded-none border-border"
                    data-testid="custom-amount-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Minimum £10
                </p>
              </div>

              {/* Preview Card */}
              <div className="mt-8 bg-muted p-8 border border-border relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-secondary/20 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="relative">
                  <Gift className="w-8 h-8 text-primary mb-4" />
                  <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2">
                    Gift Voucher
                  </p>
                  <p className="font-serif text-4xl text-foreground mb-4">
                    £{getFinalAmount()}
                  </p>
                  <p className="font-serif text-lg text-foreground">
                    Sacred Sounds Studio
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <form onSubmit={handleSubmit} className="space-y-6">
                <h2 className="font-serif text-2xl mb-6">Your Details</h2>
                
                <div>
                  <Label htmlFor="purchaserName" className="text-sm mb-2 block">
                    Your Name *
                  </Label>
                  <Input
                    id="purchaserName"
                    name="purchaserName"
                    value={formData.purchaserName}
                    onChange={handleInputChange}
                    placeholder="Your name"
                    className="rounded-none border-border"
                    required
                    data-testid="purchaser-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="purchaserEmail" className="text-sm mb-2 block">
                    Your Email *
                  </Label>
                  <Input
                    id="purchaserEmail"
                    name="purchaserEmail"
                    type="email"
                    value={formData.purchaserEmail}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    className="rounded-none border-border"
                    required
                    data-testid="purchaser-email-input"
                  />
                </div>

                <div className="pt-4 border-t border-border">
                  <h3 className="font-serif text-lg mb-4">Recipient Details (optional)</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="recipientName" className="text-sm mb-2 block">
                        Recipient's Name
                      </Label>
                      <Input
                        id="recipientName"
                        name="recipientName"
                        value={formData.recipientName}
                        onChange={handleInputChange}
                        placeholder="Who is this gift for?"
                        className="rounded-none border-border"
                        data-testid="recipient-name-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="recipientEmail" className="text-sm mb-2 block">
                        Recipient's Email
                      </Label>
                      <Input
                        id="recipientEmail"
                        name="recipientEmail"
                        type="email"
                        value={formData.recipientEmail}
                        onChange={handleInputChange}
                        placeholder="Send directly to recipient"
                        className="rounded-none border-border"
                        data-testid="recipient-email-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-sm mb-2 block">
                        Personal Message
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Add a personal message to the voucher"
                        className="rounded-none border-border min-h-[100px]"
                        data-testid="gift-message-input"
                      />
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full rounded-none bg-primary hover:bg-primary/90 text-primary-foreground py-6 text-xs tracking-[0.2em] uppercase"
                  data-testid="purchase-voucher-btn"
                >
                  {isLoading ? (
                    "Processing..."
                  ) : (
                    <>
                      <CreditCard className="mr-2 w-4 h-4" />
                      Purchase for £{getFinalAmount()}
                    </>
                  )}
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  You will receive the voucher code via email after payment.
                </p>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
